import { X } from 'lucide-react';
import type { GuestRelationKind } from '@peles/shared';
import {
  useDeleteGuestRelation,
  useGuestRelations,
  useSetGuestRelation,
} from '../../../hooks/useEventGuests';
import { useDialog } from '../../../context/DialogContext';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { GuestSearch } from './GuestSearch';

export interface GuestRelationsEditorProps {
  eventId: string;
  guestId: string;
}

const KINDS: Array<{ kind: GuestRelationKind; title: string; placeholder: string }> = [
  { kind: 'TOGETHER', title: 'Keep together with', placeholder: 'Find a guest to seat with' },
  { kind: 'APART', title: 'Keep apart from', placeholder: 'Find a guest to seat away from' },
];

/**
 * Who this guest should sit with, and away from — saved as they are chosen,
 * one wish per pair, and read by seating.
 */
export function GuestRelationsEditor({ eventId, guestId }: GuestRelationsEditorProps) {
  const { data: relations = [] } = useGuestRelations(eventId);
  const setRelation = useSetGuestRelation(eventId);
  const removeRelation = useDeleteGuestRelation(eventId);
  const { alert } = useDialog();

  const mine = relations.filter((r) => r.guestId === guestId || r.otherGuestId === guestId);
  const otherOf = (r: (typeof mine)[number]) =>
    r.guestId === guestId
      ? { id: r.otherGuestId, name: r.otherGuestName }
      : { id: r.guestId, name: r.guestName };
  const paired = mine.map((r) => otherOf(r).id);

  const fail = (err: unknown) =>
    alert({
      title: 'Could not save',
      message: extractApiErrorMessage(err, 'The seating wish was not saved.'),
      variant: 'error',
    });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {KINDS.map(({ kind, title, placeholder }) => (
        <div key={kind}>
          <span className="block text-sm font-medium text-gray-700 mb-1">{title}</span>
          <ul className="flex flex-wrap gap-1.5 mb-2">
            {mine
              .filter((r) => r.kind === kind)
              .map((r) => (
                <li
                  key={r.id}
                  className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-sm ${
                    kind === 'TOGETHER' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}
                >
                  {otherOf(r).name}
                  <button
                    type="button"
                    onClick={() => removeRelation.mutateAsync(r.id).catch(fail)}
                    aria-label={`Remove ${otherOf(r).name}`}
                    className="p-0.5 rounded-full hover:bg-white/60"
                  >
                    <X size={14} aria-hidden />
                  </button>
                </li>
              ))}
          </ul>
          <GuestSearch
            eventId={eventId}
            excludeIds={[guestId, ...paired]}
            placeholder={placeholder}
            ariaLabel={title}
            onChoose={(other) =>
              void setRelation.mutateAsync({ guestId, otherGuestId: other.id, kind }).catch(fail)
            }
          />
        </div>
      ))}
    </div>
  );
}
